import React, { useReducer, useRef } from 'react';
import { Placeholder } from '@thd-olt-component-react/core-ui';
import { RatingMeter } from '@one-thd/sui-atomic-components';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useProductReviews } from '../useProductReviews';
import { dataModel } from '../ReviewDataModel';
import { initialState } from '../../constants';
import { stateReducer } from '../../reducer';
import { RatingsAndReviewsDefaultProps, RatingsAndReviewsPropTypes } from '../RatingsAndReviewsPropTypes';
import ReviewHelper from '../Review/helpers/ReviewHelper';

import '../ratings-and-reviews.style.scss';

const RatingsAndReviewsAccordionTitlePlaceholder = (
  <div data-component="RatingsAndReviewsAccordionTitlePlaceholder">
    <Placeholder
      type="rect"
      height="50px"
      width="100%"
    />
  </div>
);

export const RatingsAndReviewsAccordionTitleComponent = ({
  apiKey, itemId, seoPageNumber, category = 'product', noSpacing
}) => {
  const [state] = useReducer(stateReducer, initialState);
  const persistedStatistics = useRef({});

  const {
    averageRating, loading, statistics, totalReviews
  } = useProductReviews({ itemId, seoPageNumber, ...state });

  if (loading) {
    return RatingsAndReviewsAccordionTitlePlaceholder;
  }

  const reviewStatistics = persistedStatistics.current && persistedStatistics.current[itemId]
    ? persistedStatistics.current[itemId]
    : statistics;

  const {
    averageOverallRating,
    totalReviewCount
  } = reviewStatistics || {};

  // Use BV as primary but fallback to available product data
  const total = totalReviewCount || totalReviews;
  const totalWithCommas = ReviewHelper.numberWithCommas(total || 0);
  const average = averageOverallRating || Number(averageRating).toFixed(1);

  return (
    <div
      className={`ratings-reviews__accordion ${noSpacing ? 'ratings-reviews__accordion--no-spacing' : ''}`}
      data-component="RatingsAndReviewsAccordionTitle"
    >
      <h3 className="ratings-reviews__accordion-header">Customer Reviews</h3>
      {totalReviewCount > 0
        && (
          <div className="ratings-reviews__accordion-subheader sui-h-5">
            <span className="sui-text-center">{average} out of 5</span>
            <RatingMeter
              value={Number(average)}
              label={`(${totalWithCommas})`}
              edge="start"
              RatingProps={{
                color: 'brand',
                size: 'small'
              }}
            />
          </div>
        )}
    </div>
  );
};

RatingsAndReviewsAccordionTitleComponent.displayName = 'RatingsAndReviewsAccordionTitleComponent';
RatingsAndReviewsAccordionTitleComponent.propTypes = RatingsAndReviewsPropTypes;
RatingsAndReviewsAccordionTitleComponent.defaultProps = RatingsAndReviewsDefaultProps;
RatingsAndReviewsAccordionTitleComponent.dataModel = dataModel;

const HydratedComponent = withHydrator({
  id: 'ratings-and-reviews-accordion-title',
  scrollBuffer: 750,
  delay: 1000,
  placeholder: RatingsAndReviewsAccordionTitlePlaceholder
}, RatingsAndReviewsAccordionTitleComponent);
const DynamicComponent = withDynamicComponent(HydratedComponent);

export const RatingsAndReviewsAccordionTitle = withErrorBoundary(DynamicComponent);

RatingsAndReviewsAccordionTitle.displayName = 'RatingsAndReviewsAccordionTitle';
RatingsAndReviewsAccordionTitle.propTypes = RatingsAndReviewsPropTypes;
RatingsAndReviewsAccordionTitle.defaultProps = RatingsAndReviewsDefaultProps;
RatingsAndReviewsAccordionTitle.dataModel = dataModel;
